// Layout/overflow calculations for the blog editor mobile preview.
//
// Extracted from BlogsPage.js. Pure functions: no React, no setters,
// no toasts. Depend only on element shape and the mobile-frame constants.

import { MOBILE_PHONE_WIDTH, MOBILE_PHONE_HEIGHT } from './blogConstants';

/**
 * Calculate the rendered height (in px) of a media element (image/video)
 * given its aspect ratio. Falls back to a default if no aspect ratio is set
 * or it can't be parsed.
 */
export const calculateMediaHeight = (element) => {
  // Calculate exact height based on aspect ratio and actual content width
  // Mobile preview has px: 2 (16px horizontal padding on each side = 32px total)
  const containerWidth = MOBILE_PHONE_WIDTH - 32; // 414px - 32px = 382px actual content width

  // Get aspect ratio from element styles
  const aspectRatio = element.styles?.aspectRatio;

  if (!aspectRatio || aspectRatio === 'auto') {
    // No aspect ratio set, use default estimate - support URLs with query params
    const src = element.mediaUrl || element.videoFile || element.videoSrc || element.content;
    return typeof src === 'string' && src.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) ? 500 : 400;
  }

  // Parse aspect ratio (e.g., "16/9", "1/1", "9/16")
  const [width, height] = aspectRatio.split('/').map(Number);

  if (!width || !height) {
    // Invalid aspect ratio, use default
    return 400;
  }

  // Calculate exact height: containerWidth * (height / width)
  const calculatedHeight = containerWidth * (height / width);

  console.log('📏 Calculated media height:', {
    aspectRatio,
    containerWidth,
    calculatedHeight: Math.round(calculatedHeight)
  });

  return Math.round(calculatedHeight);
};

/**
 * Estimate the rendered height (in px) of any blog element in the mobile preview.
 * Per-type heuristics — character counts, image counts, aspect ratios.
 */
export const calculateElementHeight = (element) => {
  let height;
  switch (element.type) {
    case 'title':
      // More accurate height calculation for mobile preview
      // Base height for h4 typography (1.4rem = ~22px) + padding + line height
      height = 80;
      // Add extra height for longer titles (roughly 1px per character for mobile)
      if (element.content) {
        height += Math.ceil(element.content.length * 0.8);
      }
      // Add padding (0.5rem top + 0.5rem bottom = ~16px)
      height += 16;
      break;
    case 'text':
      // Very generous height calculation for text elements
      // Base height for body1 typography (1rem = ~16px) + line height 1.6
      height = 60;
      // Dynamic height based on content length (very generous for mobile)
      if (element.content) {
        // Estimate lines needed (roughly 60 characters per line on mobile - very generous)
        const lines = Math.ceil(element.content.length / 60);
        height = lines * 35; // 35px per line (very generous spacing)
      }
      // Add padding (0.1rem top + 0.1rem bottom = ~3px)
      height += 3;
      // Minimum height for readability
      height = Math.max(height, 80);
      break;
    case 'media':
    case 'video_with_button_overlay':
      // Calculate exact height based on aspect ratio
      height = calculateMediaHeight(element);
      break;
    case 'offer':
      // More accurate height for offer elements
      height = 250; // Base height
      if (element.htmlContent) {
        // More accurate estimation based on HTML content length
        // HTML content is much more complex than plain text
        const contentLength = element.htmlContent.length;

        console.log('📏 Calculating offer element height:', {
          contentLength,
          htmlContent: element.htmlContent.substring(0, 100) + '...'
        });

        // Estimate based on content complexity (more conservative)
        if (contentLength < 500) {
          height = 200; // Simple offer
        } else if (contentLength < 1000) {
          height = 300; // Medium offer
        } else if (contentLength < 2000) {
          height = 400; // Complex offer
        } else {
          height = 500; // Very complex offer
        }

        // Add extra height for images and complex layouts (more conservative)
        const imageCount = (element.htmlContent.match(/<img/g) || []).length;
        const sectionCount = (element.htmlContent.match(/<div/g) || []).length;

        height += imageCount * 30; // 30px per image (reduced from 50px)
        height += Math.floor(sectionCount / 4) * 20; // Extra height for complex layouts (reduced)

        console.log('📏 Offer element height calculation:', {
          baseHeight: height,
          imageCount,
          sectionCount,
          finalHeight: height
        });
      }

      // Cap at reasonable maximum to prevent excessive height
      height = Math.min(height, 450); // Reduced max height
      height = Math.max(height, 200); // Minimum height for offer elements

      console.log('📏 Final offer element height:', height);
      break;
    case 'blackbar':
      height = 80; // More accurate for mobile
      break;
    default:
      height = 80;
      break;
  }
  return height;
};

/**
 * Sum of element heights plus inter-element spacing (16px per gap).
 */
export const calculatePageHeight = (pageElements) => {
  let totalHeight = 0;
  pageElements.forEach(element => {
    totalHeight += calculateElementHeight(element);
  });
  // Add spacing between elements (16px per element to match mb: 2)
  totalHeight += (pageElements.length - 1) * 16;
  return totalHeight;
};

/**
 * Maximum usable page height in the mobile preview.
 * NOTE: The inline comments below describe a calculation (700 - 60 - 32 = 608)
 * that does not match the current return value. Preserved as-is during extraction
 * to keep this change surgical; flag for review.
 */
export const getMaxPageHeight = () => {
  // Mobile phone height minus bottom bar (60px) and content padding (32px total)
  // The preview has py: 2 (16px top + 16px bottom) padding
  // 700px - 60px (bottom bar) - 32px (content padding) = 608px
  // Trigger overflow alert earlier by reducing available height by 50px
  return MOBILE_PHONE_HEIGHT; // 60px bottom bar + 50px early trigger buffer
};

/**
 * Returns true if the current (or hypothetical post-add) set of elements
 * would exceed the maximum page height.
 */
export const checkPageOverflow = (pageElements, newElement = null) => {
  const elementsToCheck = newElement ? [...pageElements, newElement] : pageElements;
  const pageHeight = calculatePageHeight(elementsToCheck);
  const maxHeight = getMaxPageHeight();
  const willOverflow = pageHeight > maxHeight;

  console.log('🔍 Overflow check:', {
    elementCount: elementsToCheck.length,
    pageHeight,
    maxHeight,
    willOverflow,
    elements: elementsToCheck.map(el => ({ type: el.type, content: el.content?.substring(0, 20) + '...' }))
  });

  return willOverflow;
};
